<template>
  <AppNavbar />
  <div class="vx-admin">
    <div style="max-width: 1040px">

      <div class="mb-3">
        <div class="vx-migas mb-1">
          <span>Cobranza</span><span class="sep">/</span>
          <router-link :to="{ name: 'Suscripciones' }">Suscripciones</router-link>
          <span class="sep">/</span><span>Nueva</span>
        </div>
        <h1 class="vx-titulo">Nueva suscripción</h1>
      </div>

      <!-- 01 — Esquema -->
      <div class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">01</span>
            <h2 class="vx-panel-titulo">Esquema de cobro</h2>
          </div>
        </div>
        <div class="p-3">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="vx-etiqueta" for="esquema">Esquema</label>
              <select id="esquema" v-model="form.cr_esquema_id" class="vx-campo" @change="cargarPeriodos">
                <option :value="null" disabled>Elegí un esquema…</option>
                <option v-for="e in esquemas" :key="e.cr_esquema_id" :value="e.cr_esquema_id">
                  {{ e.nombre }}
                </option>
              </select>
              <div v-if="errorDe('cr_esquema_id')" class="vx-error">{{ errorDe('cr_esquema_id') }}</div>
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="vigencia">Vigencia desde</label>
              <input id="vigencia" v-model="form.vigencia_desde" class="vx-campo" type="date"
                     :class="{ 'vx-campo-error': errorDe('vigencia_desde') }">
              <div v-if="errorDe('vigencia_desde')" class="vx-error">{{ errorDe('vigencia_desde') }}</div>
              <div class="vx-ayuda mt-1">
                Sólo se cobran los períodos que vencen después de esta fecha.
              </div>
            </div>
            <!--
              CR-E7-002 / RN-53. La columna existía en la base desde E1 y la
              generación ya filtraba por ella: lo único que faltaba era este
              campo. Es lo que define hasta dónde se le cobra a esta persona.
            -->
            <div class="col-md-3">
              <label class="vx-etiqueta" for="vigencia-hasta">Vigencia hasta</label>
              <input id="vigencia-hasta" v-model="form.vigencia_hasta" class="vx-campo" type="date"
                     :min="form.vigencia_desde">
              <div class="vx-ayuda mt-1">
                Opcional. Sin fecha, se cobran todos los períodos del esquema.
              </div>
            </div>
          </div>

          <!-- Previsualización de períodos. El backend la calcula con la misma
               función que usa la generación, así que lo que se ve acá es
               exactamente lo que se va a generar — no una aproximación de la UI. -->
          <div v-if="periodos.length" class="vx-aviso mt-3">
            <div class="vx-aviso-titulo">
              <i class="bi bi-calendar3"></i>
              {{ resumenPeriodos }}
            </div>
            <div class="d-flex flex-wrap gap-1 mt-2">
              <span v-for="p in periodos" :key="p.numero" class="vx-chip vx-num">{{ p.etiqueta }}</span>
            </div>
            <div class="vx-ayuda mt-2">
              Primer vencimiento el {{ fechaCorta(periodos[0].fecha_vencimiento) }}.
              Si un mes no tiene ese día, vence el último.
            </div>
          </div>

          <div v-else-if="!esquemas.length && !cargandoEsquemas" class="vx-nota mt-3">
            <i class="bi bi-info-circle"></i>
            <span>
              No hay esquemas de cobro cargados todavía. Un esquema define cada cuánto se
              cobra, cuántos períodos y cuándo vencen.
            </span>
          </div>
        </div>
      </div>

      <!-- 02 — Ficha -->
      <div class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">02</span>
            <h2 class="vx-panel-titulo">Ficha</h2>
            <span class="vx-ayuda">— quién o qué se cobra</span>
          </div>
          <label class="vx-ayuda d-flex align-items-center gap-2 mb-0">
            <input v-model="usarFicha" type="checkbox">
            Cargar ficha
          </label>
        </div>

        <div v-if="usarFicha" class="p-3">
          <div class="row g-3">
            <div class="col-md-5">
              <label class="vx-etiqueta" for="f-nombre">Nombre</label>
              <input id="f-nombre" v-model.trim="ficha.nombre" class="vx-campo" placeholder="Juan Pérez"
                     :class="{ 'vx-campo-error': errorDe('ficha.nombre') }">
              <div v-if="errorDe('ficha.nombre')" class="vx-error">{{ errorDe('ficha.nombre') }}</div>
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="f-desc">Qué es</label>
              <input id="f-desc" v-model.trim="ficha.descripcion" class="vx-campo" placeholder="alumno" list="tipos-ficha">
              <datalist id="tipos-ficha">
                <option value="alumno"></option><option value="socio"></option>
                <option value="paciente"></option><option value="servicio"></option>
              </datalist>
              <div v-if="errorDe('ficha.descripcion')" class="vx-error">{{ errorDe('ficha.descripcion') }}</div>
            </div>
            <div class="col-md-4">
              <label class="vx-etiqueta" for="f-id">Identificador</label>
              <input id="f-id" v-model.trim="ficha.id" class="vx-campo vx-num" placeholder="Cédula o código"
                     :class="{ 'vx-campo-error': errorDe('ficha.id') }">
              <div v-if="errorDe('ficha.id')" class="vx-error">{{ errorDe('ficha.id') }}</div>
            </div>
          </div>

          <div class="mt-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <label class="vx-etiqueta mb-0">Detalle</label>
              <span class="vx-ayuda">Campos libres para buscar y mostrar</span>
            </div>

            <div v-for="(campo, i) in ficha.detalle" :key="i" class="mb-2">
              <div class="d-flex gap-2">
                <input
                  v-model.trim="campo.clave"
                  class="vx-campo vx-campo-sm detalle-clave"
                  style="max-width: 240px"
                  placeholder="grado"
                  :class="{ 'vx-campo-error detalle-invalido': errorDe(`ficha.detalle.${i}.clave`) }"
                >
                <input
                  v-model.trim="campo.valor"
                  class="vx-campo vx-campo-sm"
                  placeholder="3ro"
                  :class="{ 'vx-campo-error': errorDe(`ficha.detalle.${i}.valor`) }"
                >
                <button class="vx-btn vx-btn-sm" title="Quitar" @click="ficha.detalle.splice(i, 1)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
              <!-- El error trae la corrección hecha: un clic y queda bien.
                   Explicar la regla sin ofrecer el arreglo es dejarle al
                   operador un trabajo que la UI puede hacer sola. -->
              <div v-if="errorDe(`ficha.detalle.${i}.clave`)" class="vx-error d-flex align-items-center gap-2">
                <span>{{ errorDe(`ficha.detalle.${i}.clave`) }}</span>
                <button
                  v-if="campo.clave"
                  type="button"
                  class="vx-btn vx-btn-sm vx-btn-texto py-0"
                  @click="corregirClave(i)"
                >
                  Corregir
                </button>
              </div>
              <div v-else-if="errorDe(`ficha.detalle.${i}.valor`)" class="vx-error">
                {{ errorDe(`ficha.detalle.${i}.valor`) }}
              </div>
            </div>

            <button class="vx-btn vx-btn-sm vx-btn-texto" @click="ficha.detalle.push({ clave: '', valor: '' })">
              <i class="bi bi-plus-lg"></i> Agregar campo
            </button>
          </div>
        </div>

        <div v-else class="p-3 vx-nota">
          <i class="bi bi-info-circle"></i>
          <span>
            Sin ficha, la suscripción se identifica por su número y las cuotas salen
            sólo con el concepto. Se puede cargar después.
          </span>
        </div>
      </div>

      <!-- 03 — Titulares -->
      <div class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">03</span>
            <h2 class="vx-panel-titulo">Titulares</h2>
            <span class="vx-ayuda">— quién paga y quién más está vinculado</span>
          </div>
        </div>
        <div class="p-3">

          <div
            v-for="(t, i) in titulares"
            :key="t.cliente_id"
            class="d-flex align-items-center gap-2 p-2 mb-2 rounded"
            :style="estiloTitular(t)"
          >
            <div class="vx-iniciales">{{ iniciales(t.nombre_completo) }}</div>
            <div class="flex-grow-1 min-w-0">
              <div data-test="titular-nombre" style="font-size: 13.5px; font-weight: 570">{{ t.nombre_completo }}</div>
              <div class="vx-celda-sub vx-num">{{ documentoDe(t) || 'Sin documento' }}</div>
            </div>
            <select v-model="t.rol" class="vx-campo vx-campo-sm" style="width: 190px" aria-label="Rol">
              <option v-for="r in ROLES_TITULAR" :key="r.valor" :value="r.valor">{{ r.texto }}</option>
            </select>
            <button class="vx-btn vx-btn-sm" title="Quitar" @click="titulares.splice(i, 1)">
              <i class="bi bi-trash"></i>
            </button>
          </div>

          <!-- Buscador de personas -->
          <div class="position-relative" style="max-width: 420px">
            <input
              id="buscador-personas"
              v-model.trim="busqueda"
              class="vx-campo vx-campo-sm"
              placeholder="Buscar persona por nombre o documento…"
              :class="{ 'vx-campo-error': errorDe('titulares') }"
              @input="buscar"
            >
            <ul
              v-if="resultados.length"
              class="list-unstyled position-absolute w-100 mb-0 vx-panel"
              data-test="resultados-personas"
              style="z-index: 20; box-shadow: var(--vx-sombra); max-height: 260px; overflow-y: auto"
            >
              <li
                v-for="c in resultados"
                :key="c.cliente_id"
                class="px-3 py-2 border-bottom"
                style="cursor: pointer"
                @click="agregar(c)"
              >
                <div style="font-size: 13px; font-weight: 550">{{ c.nombre_completo }}</div>
                <div class="vx-celda-sub vx-num">{{ documentoDe(c) || 'Sin documento' }}</div>
              </li>
            </ul>
          </div>

          <!--
            CR-E6-003 / CF18. El caso real: el operador tiene la ficha cargada
            y descubre recién acá que el padre no está en el sistema. Sin esto
            tenía que irse a Clientes, darlo de alta, y volver a empezar el
            formulario desde cero.
          -->
          <button
            type="button"
            class="vx-btn vx-btn-sm mt-2"
            data-test="btn-crear-persona"
            @click="abrirAltaPersona"
          >
            <i class="bi bi-person-plus"></i>
            {{ busqueda ? `Crear a «${busqueda}»` : 'La persona no existe todavía' }}
          </button>

          <div v-if="errorDe('titulares')" class="vx-error mt-2">{{ errorDe('titulares') }}</div>

          <div class="vx-nota mt-3">
            <i class="bi bi-info-circle"></i>
            <span>
              Una suscripción tiene un único responsable de pago. Los cotitulares y
              contactos quedan vinculados pero no reciben el documento.
            </span>
          </div>
        </div>
      </div>

      <!-- 04 — Documento -->
      <div class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">04</span>
            <h2 class="vx-panel-titulo">Documento</h2>
          </div>
        </div>
        <div class="p-3 d-flex gap-2 flex-wrap">
          <button
            v-for="d in DOCUMENTOS"
            :key="d.valor"
            class="text-start p-3 rounded flex-grow-1"
            style="min-width: 260px; cursor: pointer; background: transparent"
            :style="estiloDocumento(d.valor)"
            @click="form.documento_preferido = d.valor"
          >
            <div style="font-size: 13.5px; font-weight: 600">{{ d.titulo }}</div>
            <div class="vx-ayuda mt-1">{{ d.nota }}</div>
          </button>
        </div>
      </div>

      <!--
        05 — Becas y descuentos (DA-F6, RN-77).

        **Es el camino preferente, no un atajo.** El alta genera el año lectivo
        completo en su propia transacción (D28, RN-51) y congela el descuento
        vigente en cada cuota (RN-16). Cargado acá, el descuento entra ANTES de
        que exista una sola deuda: no hay nada que recalcular después ni nada
        que explicarle a nadie.

        Hasta el 2026-09-11 esta sección no existía y el backend ya aceptaba
        `descuentos` en el payload. Esa era media iniciativa R.
      -->
      <div class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">05</span>
            <h2 class="vx-panel-titulo">Becas y descuentos</h2>
          </div>
          <button class="vx-btn vx-btn-sm" data-test="btn-agregar-descuento"
                  @click="agregarDescuento">
            <i class="bi bi-plus-lg"></i> Agregar
          </button>
        </div>

        <div v-if="!descuentos.length" class="p-3 vx-ayuda">
          Si esta familia tiene beca, cargala <strong>acá</strong>: se aplica a las
          cuotas que el alta va a generar. Cargarla después obliga a recalcular.
        </div>

        <div v-for="(d, i) in descuentos" :key="i" class="p-3 border-bottom">
          <div class="row g-2">
            <div class="col-md-3">
              <label class="vx-etiqueta" :for="`bd-tipo-${i}`">Tipo</label>
              <select :id="`bd-tipo-${i}`" v-model="d.tipo" class="vx-campo vx-campo-sm">
                <option v-for="tp in TIPOS_DESCUENTO" :key="tp.valor" :value="tp.valor">
                  {{ tp.texto }}
                </option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="vx-etiqueta" :for="`bd-valor-${i}`">
                {{ d.tipo === 'PORCENTAJE' ? '%' : 'Monto' }}
              </label>
              <input :id="`bd-valor-${i}`" v-model.number="d.valor"
                     class="vx-campo vx-campo-sm vx-num" type="number" min="0"
                     :data-test="`descuento-valor-${i}`">
            </div>
            <div class="col-md-4">
              <label class="vx-etiqueta" :for="`bd-motivo-${i}`">Motivo</label>
              <input :id="`bd-motivo-${i}`" v-model.trim="d.motivo"
                     class="vx-campo vx-campo-sm" placeholder="Beca por hermanos"
                     :data-test="`descuento-motivo-${i}`">
            </div>
            <div class="col-md-2">
              <label class="vx-etiqueta" :for="`bd-desde-${i}`">Desde</label>
              <input :id="`bd-desde-${i}`" v-model="d.vigencia_desde"
                     class="vx-campo vx-campo-sm" type="date">
            </div>
            <div class="col-md-1 d-flex align-items-end">
              <button class="vx-btn vx-btn-sm w-100" title="Quitar"
                      @click="descuentos.splice(i, 1)">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
          <div v-if="errorDescuento(d)" class="vx-error mt-1">{{ errorDescuento(d) }}</div>
        </div>

        <!-- La palanca que el colegio ya usa y está medida: un descuento que
             arranca el 1 de marzo no alcanza a la matrícula de febrero. -->
        <div v-if="descuentos.length" class="p-3 vx-nota">
          <i class="bi bi-info-circle"></i>
          <span>
            El descuento cae sobre <strong>todos los conceptos</strong> del período que
            alcanza. Si no debe tocar la matrícula, poné una fecha «desde» posterior
            a su vencimiento.
          </span>
        </div>
      </div>

      <!-- Errores del backend, con su texto tal cual: ya vienen redactados
           para el operador (error-payload-standard.md). -->
      <div v-if="error" class="alert alert-danger py-2 px-3" style="font-size: 13.5px" role="alert">
        {{ error }}
      </div>

      <div class="d-flex justify-content-end gap-2">
        <button class="vx-btn" :disabled="guardando" @click="$router.back()">Cancelar</button>
        <!-- El botón NO se deshabilita: un botón muerto no explica nada y deja
             al operador buscando qué falta. Al apretarlo, la pantalla marca
             los campos y lleva el foco al primero.
             Tampoco lleva `aria-disabled`: el botón SÍ hace algo al hacer
             clic —revela los errores—, y anunciarlo como inactivo a un lector
             de pantalla sería mentir sobre lo que pasa al apretarlo. -->
        <button
          class="vx-btn vx-btn-primario"
          :disabled="guardando"
          @click="guardar"
        >
          <span v-if="guardando" class="spinner-border spinner-border-sm"></span>
          {{ guardando ? 'Creando…' : 'Crear suscripción' }}
        </button>
      </div>

      <div v-if="!puedeGuardar && tocado" class="vx-error text-end mt-2">{{ motivoBloqueo }}</div>
      <div v-else-if="!puedeGuardar" class="vx-ayuda text-end mt-2">{{ motivoBloqueo }}</div>
    </div>

    <!--
      El mismo modal de Clientes, sin tocarlo (artículo 1). Emite `saved` con
      el cliente creado, que es lo que permite dejarlo seleccionado sin
      recargar el buscador. `OrderEditorForm` ya lo usaba con este patrón.
    -->
    <RegistrarClienteModal
      :open="altaPersonaAbierta"
      :prefill-documento="documentoTecleado"
      @saved="onPersonaCreada"
      @close="altaPersonaAbierta = false"
    />
  </div>
</template>

<script setup>
// Alta de suscripción — CR-E1-006.
// SPEC RN-3, RN-8, RN-10, RN-38 · CF2, CF3 · diseño aprobado 2026-08-31.

import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import AppNavbar from '@/components/AppNavbar.vue'
import { useNotificationStore } from '@/stores/notifications'
import {
  listarEsquemas, obtenerEsquema, crearSuscripcion, buscarClientes,
  ROLES_TITULAR, TIPOS_DESCUENTO, iniciales, documentoDe, fechaCorta, formatearMonto
} from '@/services/cobranzaService'
import { validarAlta, primerCampoConError, sugerirSnake } from '@/services/cobranzaSchemas'
import RegistrarClienteModal from '@/components/RegistrarClienteModal.vue'
import { fechaIsoLocal } from '@/utils/fechaLocal.mjs'

const router = useRouter()
const notificaciones = useNotificationStore()

const DOCUMENTOS = [
  { valor: 'FACTURA', titulo: 'Factura electrónica', nota: 'Requiere RUC o cédula del responsable' },
  { valor: 'RECIBO', titulo: 'Recibo de dinero', nota: 'Firmado digitalmente. Admite pagador sin documento' }
]

const esquemas = ref([])
const cargandoEsquemas = ref(true)
const periodos = ref([])
const titulares = ref([])
const busqueda = ref('')
const resultados = ref([])
const usarFicha = ref(true)
const guardando = ref(false)
const error = ref('')

const form = reactive({
  cr_esquema_id: null,
  vigencia_desde: fechaIsoLocal(),
  vigencia_hasta: '',
  documento_preferido: 'FACTURA'
})

const ficha = reactive({ nombre: '', descripcion: '', id: '', detalle: [{ clave: '', valor: '' }] })

// RN-77 — las becas que ya se conocen al inscribir.
const descuentos = ref([])

const agregarDescuento = () => descuentos.value.push({
  tipo: 'PORCENTAJE', valor: null, motivo: '', vigencia_desde: '', vigencia_hasta: ''
})

// Validación de forma, la misma que el backend aplica por descuento. El tope de
// conjunto —que dos becas del 60% no sumen 120%— lo valida el servidor, que es
// donde vive la regla: repetirlo acá sería una segunda definición que se separa
// en la primera corrección.
function errorDescuento (d) {
  if (!(Number(d.valor) > 0)) return 'El valor tiene que ser mayor a cero.'
  if (d.tipo === 'PORCENTAJE' && Number(d.valor) > 100) return 'Un porcentaje no puede pasar de 100.'
  if (!String(d.motivo || '').trim()) return 'Indicá el motivo: es lo que explica, en seis meses, por qué paga menos.'
  return ''
}

const descuentosValidos = computed(() => descuentos.value.every((d) => !errorDescuento(d)))

// La validación vive en `cobranzaSchemas.js`: un solo lugar, declarativo, y
// con la ruta del campo que falló — que es lo que permite llevar el foco.
const responsables = computed(() => titulares.value.filter((t) => t.rol === 'RESPONSABLE_PAGO'))

// Orden de lectura del formulario. Zod reporta los problemas en el orden de su
// esquema; el operador lee de arriba hacia abajo, y el foco tiene que ir al
// primero que él encontraría.
const ORDEN_CAMPOS = ['cr_esquema_id', 'vigencia_desde', 'ficha.nombre',
  'ficha.descripcion', 'ficha.id', 'titulares']

// Ancla de cada campo en el DOM, para poder llevar el foco.
const ANCLAS = {
  cr_esquema_id: '#esquema',
  vigencia_desde: '#vigencia',
  'ficha.nombre': '#f-nombre',
  'ficha.descripcion': '#f-desc',
  'ficha.id': '#f-id',
  titulares: '#buscador-personas'
}

const paraValidar = computed(() => ({
  cr_esquema_id: form.cr_esquema_id ?? undefined,
  vigencia_desde: form.vigencia_desde || '',
  documento_preferido: form.documento_preferido,
  usarFicha: usarFicha.value,
  ficha: usarFicha.value
    ? { nombre: ficha.nombre, descripcion: ficha.descripcion, id: ficha.id, detalle: ficha.detalle }
    : null,
  titulares: titulares.value.map((t) => ({ cliente_id: t.cliente_id, rol: t.rol }))
}))

// Todos los errores, siempre calculados. Lo que cambia es CUÁNDO se muestran:
// `tocado` evita que el formulario grite antes de que el operador escriba nada.
const erroresTodos = computed(() => validarAlta(paraValidar.value))

const tocado = ref(false)
const errores = computed(() => (tocado.value ? erroresTodos.value : {}))
const errorDe = (campo) => errores.value[campo] || ''

const puedeGuardar = computed(
  () => Object.keys(erroresTodos.value).length === 0 && descuentosValidos.value
)

const motivoBloqueo = computed(() => {
  const primero = primerCampoConError(erroresTodos.value, ORDEN_CAMPOS)
  return primero ? erroresTodos.value[primero] : ''
})

// Lleva el foco al primer campo con problema y lo deja a la vista. Es la
// diferencia entre "algo está mal" y "esto está mal, acá".
function irAlPrimerError () {
  tocado.value = true
  const campo = primerCampoConError(erroresTodos.value, ORDEN_CAMPOS)
  const selector = ANCLAS[campo] || (campo?.startsWith('ficha.detalle') ? '.detalle-invalido' : null)
  if (!selector) return
  nextTick(() => {
    const el = document.querySelector(selector)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (typeof el.focus === 'function') el.focus({ preventScroll: true })
  })
}

// La clave del detalle, corregida a snake_case de un clic.
function corregirClave (i) {
  ficha.detalle[i].clave = sugerirSnake(ficha.detalle[i].clave)
}

const resumenPeriodos = computed(() => {
  if (!periodos.value.length) return ''
  const n = periodos.value.length
  return `Genera ${n} ${n === 1 ? 'cuota' : 'cuotas'}, de ${periodos.value[0].etiqueta} a ${periodos.value[n - 1].etiqueta}`
})

const estiloTitular = (t) => t.rol === 'RESPONSABLE_PAGO'
  ? { border: '1px solid rgba(7,167,225,0.35)', background: 'rgba(7,167,225,0.04)' }
  : { border: '1px solid var(--vx-borde)', background: 'var(--vx-panel)' }

const estiloDocumento = (v) => form.documento_preferido === v
  ? { border: '1.5px solid var(--vx-acento)', background: 'rgba(7,167,225,0.05)' }
  : { border: '1px solid var(--vx-borde-fuerte)' }

let temporizador = null
function buscar () {
  clearTimeout(temporizador)
  if (busqueda.value.length < 2) { resultados.value = []; return }
  // Con debounce: sin esto son cinco consultas mientras se escribe un apellido.
  temporizador = setTimeout(async () => {
    try {
      const encontrados = await buscarClientes(busqueda.value)
      const yaPuestos = new Set(titulares.value.map((t) => t.cliente_id))
      resultados.value = encontrados.filter((c) => !yaPuestos.has(c.cliente_id))
    } catch {
      resultados.value = []
    }
  }, 250)
}

function agregar (cliente) {
  titulares.value.push({
    ...cliente,
    // El primero entra como responsable, que es el caso normal; el resto como
    // cotitular. Siempre se puede cambiar con el select.
    rol: responsables.value.length === 0 ? 'RESPONSABLE_PAGO' : 'COTITULAR'
  })
  busqueda.value = ''
  resultados.value = []
}

// --- Alta de una persona que todavía no existe (CR-E6-003, CF18) ----------
//
// Lo importante acá NO es abrir el modal: es que al volver, el formulario
// siga como estaba. Esquema, vigencia, ficha y titulares ya cargados son
// `ref`/`reactive` de este componente y el modal es un hijo, así que nada se
// pierde — pero eso es una propiedad del diseño, no una casualidad, y por eso
// el criterio de aceptación de CF18 dice "sin perder lo ya cargado".

const altaPersonaAbierta = ref(false)

// Si lo tecleado parece un documento, se lo pasa al modal ya cargado. Si es un
// nombre, no: el modal lo trataría como número de documento.
const documentoTecleado = computed(
  () => (/^[\d.-]+$/.test(busqueda.value) ? busqueda.value.replace(/\D/g, '') : '')
)

// Qué se le dice al operador cuando el alta termina — D28.
//
// Cero cuotas NO se calla: pasa cuando la vigencia arranca después del último
// vencimiento del esquema (RN-53), y es un aviso, no un éxito silencioso. Una
// suscripción sin cuotas se ve idéntica a una normal en el listado.
function mensajeDelAlta (creada) {
  const g = creada?.generacion
  if (!g) return 'Suscripción creada.'
  if (g.sin_periodos) {
    return 'Suscripción creada, pero SIN cuotas: la vigencia arranca después '
      + 'del último período del esquema. Revisá las fechas.'
  }
  return `Suscripción creada con ${g.cantidad} `
    + `${g.cantidad === 1 ? 'cuota' : 'cuotas'} por ${formatearMonto(g.monto_total)}.`
}

function abrirAltaPersona () {
  resultados.value = []
  altaPersonaAbierta.value = true
}

function onPersonaCreada (cliente) {
  altaPersonaAbierta.value = false
  // El modal devuelve la fila creada; se la agrega igual que a una encontrada
  // por el buscador, así que hereda la misma regla de rol (el primero es
  // responsable). Si viniera sin `cliente_id`, agregarla dejaría un titular
  // que el backend rechazaría recién al guardar: mejor no agregar nada y que
  // el operador lo busque, que es un camino que funciona.
  if (cliente?.cliente_id) agregar(cliente)
}

async function cargarPeriodos () {
  periodos.value = []
  if (!form.cr_esquema_id) return
  try {
    const r = await obtenerEsquema(form.cr_esquema_id)
    periodos.value = r?.periodos || []
  } catch {
    periodos.value = []
  }
}

async function guardar () {
  // Segunda barrera: aunque el botón esté habilitado, no se envía nada que el
  // esquema rechace. Si algo falla, el foco va al campo.
  if (!puedeGuardar.value) { irAlPrimerError(); return }

  guardando.value = true
  error.value = ''
  try {
    const payload = {
      cr_esquema_id: form.cr_esquema_id,
      vigencia_desde: form.vigencia_desde,
      // Vacío es `null`: la columna es nullable y significa «todos los
      // períodos del esquema» (RN-53). Mandar '' rompería el DATE.
      vigencia_hasta: form.vigencia_hasta || null,
      documento_preferido: form.documento_preferido,
      ficha: usarFicha.value
        ? {
            cabecera: { nombre: ficha.nombre, descripcion: ficha.descripcion, id: ficha.id },
            detalle: Object.fromEntries(
              ficha.detalle.filter((d) => d.clave && d.valor).map((d) => [d.clave, d.valor])
            )
          }
        : null,
      ficha_tipo: usarFicha.value ? ficha.descripcion : null,
      titulares: titulares.value.map((t) => ({ cliente_id: t.cliente_id, rol: t.rol })),
      // RN-77: van en el alta, donde el backend ya los aceptaba y donde se
      // aplican ANTES de generar. Vacío se manda como `[]`, no como undefined.
      descuentos: descuentos.value.map((d) => ({
        tipo: d.tipo,
        valor: d.valor,
        motivo: d.motivo,
        vigencia_desde: d.vigencia_desde || null,
        vigencia_hasta: d.vigencia_hasta || null
      }))
    }

    const creada = await crearSuscripcion(payload)

    // El alta ya generó las cuotas (D28). Decirlo es la mitad del cambio: sin
    // el número, el operador no tiene forma de saber si el paso que antes
    // hacía a mano quedó hecho — y lo va a seguir haciendo por las dudas.
    notificaciones.notify({ type: 'success', message: mensajeDelAlta(creada) })
    router.push({ name: 'SuscripcionDetalle', params: { id: creada.cr_suscripcion_id } })
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo crear la suscripción.'
  } finally {
    guardando.value = false
  }
}

onMounted(async () => {
  try {
    esquemas.value = await listarEsquemas()
    if (esquemas.value.length === 1) {
      form.cr_esquema_id = esquemas.value[0].cr_esquema_id
      await cargarPeriodos()
    }
  } catch (err) {
    error.value = err?.userMessage || 'No se pudieron cargar los esquemas.'
  } finally {
    cargandoEsquemas.value = false
  }
})
</script>
